import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { useT } from "../i18n/LanguageContext";
import BgLogo from "./BgLogo";
import LanguageSwitcher from "./LanguageSwitcher";
import { Menu, X, ArrowUpRight } from "lucide-react";
const links = [
  { to: "/", key: "nav.home", testId: "nav-home" },
  { to: "/about", key: "nav.about", testId: "nav-about" },
  { to: "/services", key: "nav.services", testId: "nav-services" },
  { to: "/simp", key: "nav.simp", testId: "nav-simp" },
  { to: "/industries", key: "nav.industries", testId: "nav-industries" },
  { to: "/projects", key: "nav.projects", testId: "nav-projects" },
  { to: "/careers", key: "nav.careers", testId: "nav-careers" },
  { to: "/contact", key: "nav.contact", testId: "nav-contact" },
];

export default function Header() {
  const { t } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors ${
        scrolled ? "border-b border-white/10 bg-black/70 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-12 lg:px-16">
        <Link to="/" className="focus-red" data-testid="header-logo-link">
          <BgLogo />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testId}
              className={({ isActive }) =>
                `px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                  isActive ? "text-brand-red" : "text-white/80 hover:text-white"
                }`
              }
            >
              {t(l.key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher />
          <Link
            to="/contact"
            data-testid="header-cta"
            className="group inline-flex items-center gap-2 bg-brand-red px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-redHover"
          >
            {t("nav.cta")}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="border border-white/15 p-2 text-white"
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-[100] flex flex-col overflow-y-auto bg-black text-white lg:hidden"
          data-testid="mobile-menu"
          style={{ backgroundColor: "#050505" }}
        >
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-black px-6 py-4">
            <BgLogo />
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="border border-white/15 p-2 text-white hover:border-brand-red hover:text-brand-red"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex flex-col gap-1 px-6 py-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-${l.testId}`}
                className={({ isActive }) =>
                  `border-b border-white/10 py-4 font-heading text-2xl transition-colors ${
                    isActive ? "text-brand-red" : "text-white"
                  }`
                }
              >
                {t(l.key)}
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto flex flex-col gap-4 border-t border-white/10 bg-black px-6 py-6 sm:flex-row sm:items-center sm:justify-between">
            <LanguageSwitcher />
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-cta"
              className="inline-flex items-center justify-center bg-brand-red px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white"
            >
              {t("nav.cta")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
